'use client'

import { Component, ReactNode } from 'react'
import { AlertCircle, RefreshCw, Home, Bug } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onReset?: () => void
  level?: 'page' | 'component' | 'root'
  showDetails?: boolean
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: React.ErrorInfo
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details
    console.error('Error Boundary Caught Error:', {
      error,
      errorInfo,
      componentStack: errorInfo.componentStack,
      level: this.props.level || 'component',
      timestamp: new Date().toISOString(),
    })

    // Store error info in state
    this.setState({
      errorInfo,
    })

    // TODO: Send to error monitoring service (e.g., Sentry, LogRocket, etc.)
    // Example:
    // if (typeof window !== 'undefined') {
    //   window.analytics?.track('Error Occurred', {
    //     error: error.message,
    //     stack: error.stack,
    //     componentStack: errorInfo.componentStack,
    //     level: this.props.level,
    //   })
    // }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined })
    this.props.onReset?.()
  }

  handleReportError = () => {
    const { error, errorInfo } = this.state
    const errorReport = {
      message: error?.message,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    }

    // Log to console for now
    console.log('Error Report:', errorReport)

    // TODO: Send to error reporting endpoint
    // fetch('/api/errors/report', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(errorReport),
    // })

    alert('Error report logged to console. In production, this would be sent to our monitoring service.')
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      const { level = 'component', showDetails = process.env.NODE_ENV === 'development' } = this.props
      const { error, errorInfo } = this.state

      // Root level error - full page takeover
      if (level === 'root') {
        return (
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
            <Card className="max-w-2xl w-full p-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/20 mb-6">
                  <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
                </div>

                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                  Application Error
                </h1>

                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  We encountered an unexpected error. This has been logged and our team will investigate.
                </p>

                {showDetails && error && (
                  <div className="mb-6 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg text-left">
                    <p className="font-mono text-sm text-red-600 dark:text-red-400 mb-2">
                      {error.message}
                    </p>
                    {error.stack && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200">
                          View stack trace
                        </summary>
                        <pre className="mt-2 text-xs text-gray-600 dark:text-gray-400 overflow-auto max-h-40">
                          {error.stack}
                        </pre>
                      </details>
                    )}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={this.handleReset} size="lg">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Try Again
                  </Button>

                  <Button
                    onClick={() => window.location.href = '/'}
                    variant="outline"
                    size="lg"
                  >
                    <Home className="h-4 w-4 mr-2" />
                    Go Home
                  </Button>

                  <Button
                    onClick={this.handleReportError}
                    variant="outline"
                    size="lg"
                  >
                    <Bug className="h-4 w-4 mr-2" />
                    Report Error
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )
      }

      // Page level error - contained within layout
      if (level === 'page') {
        return (
          <div className="min-h-[60vh] flex items-center justify-center p-4">
            <Card className="max-w-xl w-full p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20">
                    <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Page Error
                  </h2>

                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    This page encountered an error and couldn't load properly. Please try again or navigate to another page.
                  </p>

                  {showDetails && error && (
                    <div className="mb-4 p-3 bg-gray-100 dark:bg-gray-800 rounded">
                      <p className="font-mono text-sm text-red-600 dark:text-red-400">
                        {error.message}
                      </p>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <Button onClick={this.handleReset} size="sm">
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                      Try Again
                    </Button>

                    <Button
                      onClick={() => window.history.back()}
                      variant="outline"
                      size="sm"
                    >
                      Go Back
                    </Button>

                    <Button
                      onClick={this.handleReportError}
                      variant="ghost"
                      size="sm"
                    >
                      <Bug className="h-3.5 w-3.5 mr-1.5" />
                      Report
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )
      }

      // Component level error - minimal UI disruption
      return (
        <Card className="p-4 m-2 border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/10">
          <div className="flex items-center gap-3 mb-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Component Error
            </h3>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            This component encountered an error. The rest of the page should still work normally.
          </p>

          {showDetails && error && (
            <div className="mb-3 p-2 bg-white dark:bg-gray-800 rounded text-xs font-mono text-red-600 dark:text-red-400">
              {error.message}
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={this.handleReset} size="sm" variant="outline">
              Try Again
            </Button>

            <Button
              onClick={this.handleReportError}
              size="sm"
              variant="ghost"
            >
              Report
            </Button>
          </div>
        </Card>
      )
    }

    return this.props.children
  }
}

// Convenience wrapper for common use cases
export function PageErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary level="page">
      {children}
    </ErrorBoundary>
  )
}

export function ComponentErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary level="component">
      {children}
    </ErrorBoundary>
  )
}

export function RootErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary level="root">
      {children}
    </ErrorBoundary>
  )
}
